import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import jobDocumentService from '../../services/JobDocumentService';
import { asTextFile, downloadFile, jobFilename } from '../../utils/jobPackage';

const FIELD =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500';

const TEXT_ACTION =
  'text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:text-slate-400 disabled:no-underline dark:disabled:text-slate-500';

const CHANNELS = [
  { key: 'indeed', label: 'Indeed' },
  { key: 'afa', label: 'Arbeitsagentur' },
];

function Tab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The Indeed and AfA ad texts of one job, to read, edit, copy into the portals
 * or download as the .txt the export shipped.
 *
 * One tab per channel, because they are pasted one portal at a time. Both are
 * kept in state, so switching tabs does not lose an edit, and one save writes
 * both.
 */
export function ChannelTextsModal({ job, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('indeed');

  const [recordId, setRecordId] = useState(null);
  const [baseName, setBaseName] = useState('');
  const [texts, setTexts] = useState({ indeed: '', afa: '' });

  useEffect(() => {
    let cancelled = false;
    jobDocumentService.getByJobId(job.id).then((result) => {
      if (cancelled) return;
      if (!result.success) {
        setError(result.error);
      } else if (result.data) {
        setRecordId(result.data.id);
        setBaseName(result.data.baseName);
        setTexts({ indeed: result.data.indeedText, afa: result.data.afaText });
        // Open where there is something to read.
        if (!result.data.indeedText && result.data.afaText) setTab('afa');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [job.id]);

  const channel = CHANNELS.find((c) => c.key === tab);
  const text = texts[tab];

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${channel.label}-Text kopiert`);
    } catch {
      toast.error('Kopieren nicht möglich. Bitte den Text markieren und selbst kopieren.');
    }
  }

  /** Downloads what is in the field, unsaved edits included. */
  function handleDownload() {
    downloadFile(asTextFile(text), jobFilename(tab, { job, documents: { baseName } }), 'text/plain;charset=utf-8');
  }

  async function handleSave() {
    setSaving(true);
    const result = await jobDocumentService.save({
      jobId: job.id,
      label: job.title || '',
      indeedText: texts.indeed,
      afaText: texts.afa,
      existingId: recordId,
    });
    setSaving(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    toast.success('Texte gespeichert');
    onSaved?.();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
      {/* Fixed height rather than max-height: the text field takes whatever is
          left, so a long ad reads in one view instead of a small scroll box. */}
      <div className="flex h-[92vh] w-full max-w-6xl flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-4 px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Indeed &amp; AfA</h2>
            <p className="truncate text-sm text-slate-500 dark:text-slate-400">
              {job.title}
              {job.role && <span className="italic"> · {job.role}</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-6 sm:px-8 sm:pb-8">
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Wird geladen …</p>
          ) : (
            <>
              {error && (
                <div className="mb-5 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              <div className="mb-5 flex shrink-0 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 w-fit">
                {CHANNELS.map((c) => (
                  <Tab key={c.key} active={tab === c.key} onClick={() => setTab(c.key)}>
                    {c.label}
                  </Tab>
                ))}
              </div>

              <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <label
                    htmlFor="channel-text"
                    className="text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Anzeigentext {channel.label}
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={handleCopy}
                      disabled={!text}
                      className={TEXT_ACTION}
                    >
                      Kopieren
                    </button>
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={!text}
                      className={TEXT_ACTION}
                    >
                      Herunterladen
                    </button>
                  </div>
                </div>
                <textarea
                  id="channel-text"
                  value={text}
                  onChange={(e) => setTexts((prev) => ({ ...prev, [tab]: e.target.value }))}
                  placeholder={`Noch kein ${channel.label}-Text hinterlegt.`}
                  className={`${FIELD} min-h-64 flex-1 resize-none leading-relaxed`}
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {text.length.toLocaleString('de-DE')} Zeichen
                </p>
              </div>

              <div className="mt-6 flex shrink-0 justify-end gap-2">
                <ModernButton variant="secondary" onClick={onClose}>
                  Abbrechen
                </ModernButton>
                <ModernButton onClick={handleSave} disabled={saving} loading={saving}>
                  Speichern
                </ModernButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
